import { AfterViewInit, Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    UntypedFormGroup,
    UntypedFormBuilder,
    Validators,
    UntypedFormArray,
    UntypedFormControl,
    AbstractControl,
    FormGroup,
    FormControl
} from '@angular/forms';
import { CdkDragDrop, CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import { forkJoin, of, from } from 'rxjs';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { remove, find } from 'lodash';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Animateur } from 'src/app/data/commission/models/animateur.model';
import { Salle } from 'src/app/data/commission/models/salle.model';
import { SallesService } from 'src/app/data/commission/services/salles/salles.service';
import { AnimateursService } from 'src/app/data/commission/services/animateurs/animateurs.service';
import { Commission } from 'src/app/data/commission/models/commission.model';
import { Jury } from 'src/app/data/commission/models/jury.model';
import { CommissionsService } from 'src/app/data/commission/services/commissions/commissions.service';
import { SessionContext } from 'src/app/core/services/config/app.settings';
import { JureJury } from 'src/app/data/commission/models/jure-jury.model';
import { Cahier } from 'src/app/data/habilitation/models/cahier.model';
import { RechercheJureComponent } from './modals/recherche-jure/recherche-jure.component';
import { RefStatutJureCode } from 'src/app/data/commission/models/enums/ref-statut-jure.enum';
import { ReferencesService } from 'src/app/data/commission/services/references/references.service';
import { StatutJure } from 'src/app/data/commission/models/statut-jure.model';
import { RechercheCdcsComponent } from './modals/recherche-cdcs/recherche-cdcs.component';
import { ModalService } from 'src/app/shared/services/modal/modal.service';
import { Fail } from 'src/app/shared/errors/fail.error';
import { RechercheEchantillonsComponent } from './modals/recherche-echantillons/recherche-echantillons.component';
import { Echantillon } from 'src/app/data/commission/models/echantillon.model';
import { Leurre } from 'src/app/data/commission/models/leurre.model';
import { EchantillonJury } from 'src/app/data/commission/models/echantillon-jury.model';
import { FunctionalError } from 'src/app/shared/errors/functional.error';
import { AffectationEchantillonsComponent } from './modals/affectation-echantillons/affectation-echantillons.component';
import { LoaderService } from 'src/app/core/services/loader/loader.service';
import { ProduitsService } from 'src/app/data/habilitation/services/produits/produits.service';
import { TypeLeurre } from 'src/app/data/commission/models/type-leurre.model';
import { OperateursService } from 'src/app/data/intervenant/services/operateurs/operateurs.service';
import { CategorieEnum } from 'src/app/data/edition/models/enums/categorie.enum.model';
import { TelechargerDocumentComponent } from '../../../main/components/telecharger-document/telecharger-document.component';
import { FiltreEchantillonsIgpComponent } from './modals/filtre-echantillons-igp/filtre-echantillons-igp.component';
import { distinctUntilChanged } from 'rxjs/operators';
import { ReferentielService } from '../../../../../data/declaration/services/referentiel/referentiel.service';
import { NatureControle } from '../../../../../data/declaration/models/nature-controle.model';
import { TypeNature } from '../../../../../data/declaration/models/enums/type-nature.enum';

const heureRequiredValidator = function (control: UntypedFormControl) {
    if (!control.value || typeof control.value.hour === 'undefined' || typeof control.value.minute === 'undefined') {
        return { invalid: true };
    }

    return null;
};

@Component({
    selector: 'app-edit-commission',
    templateUrl: './edit-commission.component.html',
    styleUrls: ['./edit-commission.component.scss'],
    standalone: false
})
export class EditCommissionComponent implements AfterViewInit {
    commission: Commission;
    salles: Salle[];
    animateurs: Animateur[];
    statutsJure: StatutJure[];
    typesLeurres: TypeLeurre[];
    indexLastEchantillon: number;
    edition: boolean;
    commissionIsOver = false;
    refNatures: NatureControle[];

    dateCreationDepuisPlanning: moment.Moment | null = null;

    private readonly statutsJureOrder = [
        RefStatutJureCode.A_CONVOQUER,
        RefStatutJureCode.CONVOQUE,
        RefStatutJureCode.INSCRIT,
        RefStatutJureCode.DESINSCRIT,
        RefStatutJureCode.EXCUSE,
        RefStatutJureCode.PRESENT,
        RefStatutJureCode.ABSENT
    ];

    get nbCdcs(): number {
        let idCdcs: number[] = [];
        return this.jurys.reduce((total, jury) => {
            const cdcs: Cahier[] = jury.get('cdcs').value.filter(cdc => !idCdcs.includes(cdc.id));
            idCdcs = idCdcs.concat(cdcs.map(cdc => cdc.id));
            return total + cdcs.length;
        }, 0);
    }
    get nbJuresInscrits(): number {
        return this.jurys.reduce((total, jury) => {
            const jures: JureJury[] = jury.get('jures').value;
            return total + jures.reduce((tt, jure) => tt + (jure.statut.code === RefStatutJureCode.INSCRIT ? 1 : 0), 0);
        }, 0);
    }
    get nbJuresPresents(): number {
        return this.jurys.reduce((total, jury) => {
            const jures: JureJury[] = jury.get('jures').value;
            return total + jures.reduce((tt, jure) => tt + (jure.statut.code === RefStatutJureCode.PRESENT ? 1 : 0), 0);
        }, 0);
    }
    get nbJuresTotal(): number {
        return this.jurys.reduce((total, jury) => {
            const jures: JureJury[] = jury.get('jures').value;
            return total + jures.length;
        }, 0);
    }
    get nbEchantillons(): number {
        return this.jurys.reduce((total, jury) => {
            const echantillons: EchantillonJury[] = jury.get('echantillons').value;
            return total + echantillons.length;
        }, 0);
    }

    formGroup: UntypedFormGroup;
    get jurys(): UntypedFormGroup[] {
        try {
            return (this.formGroup.get('jurys') as UntypedFormArray).controls as UntypedFormGroup[];
        } catch (e) {
            return [];
        }
    }

    selectedList: 'cdcs' | 'jures' | 'echantillons' = 'cdcs';

    setSelectedList(list: 'cdcs' | 'jures' | 'echantillons') {
        this.selectedList = list;
    }

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly sallesServices: SallesService,
        private readonly animateursService: AnimateursService,
        private readonly commissionsService: CommissionsService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly toastr: ToastrService,
        private readonly translate: TranslateService,
        private readonly modalService: NgbModal,
        private readonly refService: ReferencesService,
        private readonly modalsService: ModalService,
        private readonly loaderService: LoaderService,
        private readonly produitService: ProduitsService,
        private readonly operateursService: OperateursService,
        private readonly referentielService: ReferentielService
    ) {}

    @ViewChildren('echDrop', { read: CdkDropList })
    echDrops!: QueryList<CdkDropList>;
    @ViewChildren('cdcDrop', { read: CdkDropList })
    cdcDrops!: QueryList<CdkDropList>;
    @ViewChildren('jureDrop', { read: CdkDropList })
    jureDrops!: QueryList<CdkDropList>;

    ngAfterViewInit() {
        let idCommission = null;
        if (this.route.snapshot.paramMap.has('idCommission')) {
            idCommission = parseInt(this.route.snapshot.paramMap.get('idCommission'), 10);
            isNaN(idCommission) ? (this.edition = false) : (this.edition = true);
        }

        const debut = this.route.snapshot.queryParamMap.get('debut');
        if (debut) {
            const m = moment(debut);
            if (m.isValid()) {
                this.dateCreationDepuisPlanning = m;
            }
        }

        this.loadData(idCommission);

        this.connectEchantillonsDropLists();
        this.echDrops.changes.subscribe(() => this.connectEchantillonsDropLists());
        this.connectCdcDropLists();
        this.cdcDrops.changes.subscribe(() => this.connectCdcDropLists());
        this.connectJureDropLists();
        this.jureDrops.changes.subscribe(() => this.connectJureDropLists());
    }

    private connectEchantillonsDropLists(): void {
        const drops = this.echDrops.toArray();

        drops.forEach(d => {
            d.connectedTo = drops.filter(x => x !== d);
        });
    }

    private connectCdcDropLists(): void {
        const drops = this.cdcDrops.toArray();

        drops.forEach(d => {
            d.connectedTo = drops.filter(x => x !== d);
        });
    }

    private connectJureDropLists(): void {
        const drops = this.jureDrops.toArray();

        drops.forEach(d => {
            d.connectedTo = drops.filter(x => x !== d);
        });
    }

    loadData(idCommission: number) {
        forkJoin(
            isNaN(idCommission) ? of(new Commission()) : this.commissionsService.getCommissions(SessionContext.get('idOrganisme'), idCommission), // TODO if idCommission is set, get from server
            // of(new Commission()),
            this.sallesServices.getSalles(SessionContext.get('idOrganisme')),
            this.animateursService.getAnimateurs(SessionContext.get('idOrganisme')),
            this.refService.getReferences(),
            this.referentielService.getReferentiel()
        )
            .pipe(this.loaderService.operator())
            .subscribe(([commission, salles, animateurs, ref, referentiel]) => {
                this.commission = commission;
                this.salles = salles;
                this.animateurs = animateurs;
                this.statutsJure = ref.statutsJure;
                this.statutsJure.sort((a, b) => {
                    return this.statutsJureOrder.indexOf(a.code) - this.statutsJureOrder.indexOf(b.code);
                });
                this.typesLeurres = ref.typesLeurres;
                this.refNatures = referentiel.naturesControle;

                this.commissionIsOver = this.commission.terminee;

                this.createForm(commission);

                this.indexLastEchantillon = this.nbEchantillons;

                if (this.commission.jurys) {
                    this.commission.jurys.forEach(jury => {
                        jury.echantillons.sort((a: any, b: any) => {
                            if (a.ordre < b.ordre) {
                                return -1;
                            }
                            if (a.ordre > b.ordre) {
                                return 1;
                            }

                            return 0;
                        });

                        jury.echantillons.forEach(echantillon => {
                            // Si échantillon
                            if (echantillon.echantillon) {
                                this.produitService.getProduitByCode(echantillon.echantillon.codeProduit).subscribe(produit => {
                                    echantillon.echantillon.produit = produit;
                                });
                                this.operateursService
                                    .getInformationsDomaine(echantillon.echantillon.idOperateur)
                                    .subscribe(infos => (echantillon.echantillon.infosOperateur = infos));
                                this.operateursService
                                    .getOperateur(echantillon.echantillon.idOperateur)
                                    .subscribe(op => (echantillon.echantillon.operateur = op));
                            } else {
                                // Sinon (= si leurre)
                                this.produitService.getProduitByCode(echantillon.leurre.codeProduit).subscribe(produit => {
                                    echantillon.leurre.produit = produit;
                                });
                            }
                        });
                    });
                }

                this.normalizeJuryArrays();
            });
    }

    createForm(commission: Commission) {
        if (this.commission.id) {
            const selectedAnimateur = find(this.animateurs, anim => anim.id === commission.animateur.id);
            const selectedSalle = find(this.salles, salle => salle.id === commission.salle.id);
            this.formGroup = this.fb.group({
                date: [
                    {
                        year: commission.dateDebut.toDate().getFullYear(),
                        month: commission.dateDebut.toDate().getMonth() + 1,
                        day: commission.dateDebut.toDate().getDate()
                    },
                    Validators.required
                ],
                heureDebut: [
                    {
                        hour: commission.dateDebut.toDate().getHours(),
                        minute: commission.dateDebut.toDate().getMinutes()
                    },
                    heureRequiredValidator
                ],
                heureFin: [
                    {
                        hour: commission.dateFin.toDate().getHours(),
                        minute: commission.dateFin.toDate().getMinutes()
                    },
                    heureRequiredValidator
                ],
                animateur: [selectedAnimateur, Validators.required],
                salle: [selectedSalle, Validators.required],
                jurys: this.fb.array([]),
                annee: [commission.annee, Validators.required]
            });

            commission.jurys.forEach(item => {
                (this.formGroup.get('jurys') as UntypedFormArray).push(this.getJuryForm(item));
            });
        } else {
            const dateInit = this.dateCreationDepuisPlanning ?? moment();

            this.formGroup = this.fb.group({
                date: [
                    {
                        year: dateInit.year(),
                        month: dateInit.month() + 1,
                        day: dateInit.date()
                    },
                    Validators.required
                ],
                heureDebut: [
                    {
                        hour: dateInit.hour(),
                        minute: dateInit.minute()
                    },
                    heureRequiredValidator
                ],
                heureFin: [
                    {
                        hour: dateInit.clone().add(3, 'hour').hour(),
                        minute: dateInit.clone().add(3, 'hour').minute()
                    },
                    heureRequiredValidator
                ],
                animateur: [undefined, Validators.required],
                salle: [undefined, Validators.required],
                jurys: this.fb.array([]),
                annee: [dateInit.year(), Validators.required]
            });
            this.addJury();
        }

        this.formGroup
            .get('date')
            .valueChanges.pipe(distinctUntilChanged((prev, curr) => prev?.year === curr?.year))
            .subscribe(value => {
                if (value?.year) {
                    this.getField('annee').setValue(value.year, { emitEvent: false });
                }
            });
    }

    createJuryForm(): UntypedFormGroup {
        return this.fb.group({
            cdcs: this.fb.array([]),
            jures: this.fb.array([]),
            echantillons: this.fb.array([])
        });
    }

    getJuryForm(jury: Jury): UntypedFormGroup {
        return this.fb.group({
            cdcs: this.fb.array((jury.cdcs || []).map(cdc => this.fb.control(cdc))),
            jures: this.fb.array((jury.jures || []).map(jure => this.fb.control(jure))),
            echantillons: this.fb.array((jury.echantillons || []).map(ech => this.fb.control(ech)))
        });
    }

    getField(name: string) {
        return this.formGroup.get(name);
    }

    submit() {
        if (this.formGroup.valid) {
            const commission = new Commission();

            const date = this.getField('date').value;
            const heureDebut = this.getField('heureDebut').value;
            const heureFin = this.getField('heureFin').value;

            commission.dateDebut = moment([date.year, date.month - 1, date.day, heureDebut.hour, heureDebut.minute, 0, 0]);
            commission.dateFin = moment([date.year, date.month - 1, date.day, heureFin.hour, heureFin.minute, 0, 0]);
            commission.annee = this.getField('annee').value.toString();

            commission.animateur = this.getField('animateur').value;
            commission.salle = this.getField('salle').value;

            if (this.commissionIsOver) {
                commission.terminee = true;
            } else {
                commission.terminee = false;
            }

            commission.jurys = (this.getField('jurys') as UntypedFormArray).controls.map((formGroup, index) => {
                const jury = new Jury();

                jury.ordre = index + 1;
                jury.jures = formGroup.get('jures').value.map((item: any) => ({
                    idJure: item.jure?.id ?? item.idJure,
                    idStatut: item.statut?.id ?? item.idStatut,
                    idCollege: item.college?.id ?? item.idCollege
                }));
                jury.cdcs = formGroup.get('cdcs').value;
                jury.echantillons = formGroup.get('echantillons').value.map((ech: EchantillonJury, index: number) => {
                    ech.ordre = index;
                    return ech;
                });

                return jury;
            });

            if (this.edition) {
                commission.id = this.commission.id;
                this.commissionsService.modifyCommission(SessionContext.get('idOrganisme'), commission).subscribe(
                    response => {
                        this.toastr.success(
                            this.translate.instant('page.commissions.edit.alert.modification.message', { numero: response.numero }),
                            this.translate.instant('page.commissions.edit.alert.modification.title'),
                            { timeOut: 10 * 1000 }
                        );
                        this.router.navigate(['../../planning'], { relativeTo: this.route });
                    },
                    error => {
                        console.log(error);
                    }
                );
            } else {
                this.commissionsService.createCommission(SessionContext.get('idOrganisme'), commission).subscribe(response => {
                    this.toastr.success(
                        this.translate.instant('page.commissions.edit.alert.creation.message', { numero: response.numero }),
                        this.translate.instant('page.commissions.edit.alert.creation.title'),
                        { timeOut: 10 * 1000 }
                    );
                    this.router.navigate(['../../planning'], { relativeTo: this.route });
                });
            }
        } else {
            console.warn(this.formGroup);
        }
    }

    enregistrer() {
        if (this.formGroup.valid) {
            const commission = new Commission();

            const date = this.getField('date').value;
            const heureDebut = this.getField('heureDebut').value;
            const heureFin = this.getField('heureFin').value;

            commission.dateDebut = moment([date.year, date.month - 1, date.day, heureDebut.hour, heureDebut.minute, 0, 0]);
            commission.dateFin = moment([date.year, date.month - 1, date.day, heureFin.hour, heureFin.minute, 0, 0]);
            commission.annee = this.getField('annee').value.toString();

            commission.animateur = this.getField('animateur').value;
            commission.salle = this.getField('salle').value;

            if (this.commissionIsOver) {
                commission.terminee = true;
            } else {
                commission.terminee = false;
            }

            commission.jurys = (this.getField('jurys') as UntypedFormArray).controls.map((formGroup, index) => {
                const jury = new Jury();

                jury.ordre = index + 1;
                jury.jures = formGroup.get('jures').value.map((item: any) => ({
                    idJure: item.jure?.id ?? item.idJure,
                    idStatut: item.statut?.id ?? item.idStatut,
                    idCollege: item.college?.id ?? item.idCollege
                }));
                jury.cdcs = formGroup.get('cdcs').value;
                jury.echantillons = formGroup.get('echantillons').value.map((ech: EchantillonJury, index: number) => {
                    ech.ordre = index;
                    return ech;
                });

                return jury;
            });

            if (this.edition) {
                commission.id = this.commission.id;
                this.commissionsService.modifyCommission(SessionContext.get('idOrganisme'), commission).subscribe(response => {
                    this.toastr.success(
                        this.translate.instant('page.commissions.edit.alert.modification.message', { numero: response.numero }),
                        this.translate.instant('page.commissions.edit.alert.modification.title'),
                        { timeOut: 10 * 1000 }
                    );

                    this.router.navigate(['main/controles/commissions/' + response.id + '/edit']).then(() => {
                        window.location.reload();
                    });
                });
            } else {
                this.commissionsService.createCommission(SessionContext.get('idOrganisme'), commission).subscribe(response => {
                    this.toastr.success(
                        this.translate.instant('page.commissions.edit.alert.creation.message', { numero: response.numero }),
                        this.translate.instant('page.commissions.edit.alert.creation.title'),
                        { timeOut: 10 * 1000 }
                    );
                    this.router.navigate(['main/controles/commissions/' + response.id + '/edit']).then(() => {
                        window.location.reload();
                    });
                });
            }
        } else {
            console.warn(this.formGroup);
        }
    }

    getStatutIcon(code: RefStatutJureCode) {
        switch (code) {
            case RefStatutJureCode.A_CONVOQUER:
                return 'envelope-open';
            case RefStatutJureCode.CONVOQUE:
                return 'envelope';
            case RefStatutJureCode.INSCRIT:
                return 'user-plus';
            case RefStatutJureCode.EXCUSE:
                return 'user-times';
            case RefStatutJureCode.DESINSCRIT:
                return 'user-minus';
            case RefStatutJureCode.PRESENT:
                return 'user-check';
            case RefStatutJureCode.ABSENT:
                return 'user-slash';
            default:
                Fail.never(code);
        }
    }

    // ####################################
    // ############  Jurys  ###############
    // ####################################

    addJury() {
        (this.formGroup.get('jurys') as UntypedFormArray).push(this.createJuryForm());
    }

    deleteJury(index: number) {
        const jurys = this.formGroup.get('jurys') as UntypedFormArray;

        const jures = this.formGroup.get(`jurys.${index}.jures`).value;
        const cdcs = this.formGroup.get(`jurys.${index}.cdcs`).value;

        if (jurys.length > 1) {
            if (jures.length || cdcs.length) {
                this.modalsService
                    .confirm(
                        this.translate.instant('page.commissions.edit.jurys.confirm_remove.message'),
                        this.translate.instant('page.commissions.edit.jurys.confirm_remove.title'),
                        {
                            cancelText: this.translate.instant('label.annuler'),
                            submitText: this.translate.instant('label.valider')
                        }
                    )
                    .then(
                        () => {
                            jurys.removeAt(index);
                        },
                        () => {}
                    );
            } else {
                jurys.removeAt(index);
            }
        }
    }

    moveJury(event: CdkDragDrop<UntypedFormGroup>) {
        const jurys = this.formGroup.get('jurys') as UntypedFormArray;
        const jury = jurys.at(event.previousIndex);
        jurys.removeAt(event.previousIndex);
        jurys.insert(event.currentIndex, jury);
    }

    changeStatut(jure: UntypedFormControl, statut: StatutJure, event?: Event): void {
        event?.stopPropagation();

        jure.setValue({
            ...jure.value,
            statut
        });

        jure.markAsDirty();
        jure.updateValueAndValidity();
    }

    // ####################################
    // #############  cdcs  ###############
    // ####################################

    addCdc(indexJury: number) {
        const cdcsFA = this.formGroup.get(['jurys', indexJury, 'cdcs']) as UntypedFormArray;

        const modalRef = this.modalService.open(RechercheCdcsComponent, { size: 'lg', backdrop: 'static' });
        modalRef.componentInstance.idOrganisme = SessionContext.get('idOrganisme');
        modalRef.componentInstance.alreadySelectedCahiersIds = (cdcsFA ? cdcsFA.value : []).map((cahier: Cahier) => cahier.id);

        modalRef.result.then(
            (cdcs: Cahier[]) => {
                if (!cdcsFA) {
                    return;
                }

                cdcs.forEach((cdc: Cahier) => {
                    cdcsFA.push(this.fb.control(cdc));
                });

                cdcsFA.markAsDirty();
                cdcsFA.updateValueAndValidity();
            },
            (error: any) => {
                throw error;
            }
        );
    }

    removeCdc(indexJury: number, cdc: Cahier) {
        const fa = this.formGroup.get(['jurys', indexJury, 'cdcs']) as UntypedFormArray;
        if (!fa) {
            return;
        }

        const values: Cahier[] = fa.value || [];
        const idx = values.indexOf(cdc);

        if (idx >= 0) {
            fa.removeAt(idx);
            fa.markAsDirty();
            fa.updateValueAndValidity();
        }
    }

    canDropCdc = (drag: CdkDrag<any>, drop: CdkDropList<any>) => {
        const dropFa = drop.data as UntypedFormArray;
        const draggedCtrl = drag.data; // AbstractControl
        const dragged = draggedCtrl ? draggedCtrl.value : null;

        const values = dropFa ? dropFa.value : [];
        if (!dragged) {
            return true;
        }

        return values.findIndex((c: any) => c && c.id === dragged.id) === -1;
    };

    moveCdc(event: CdkDragDrop<UntypedFormArray>) {
        const fromFa = event.previousContainer.data as UntypedFormArray;
        const toFa = event.container.data as UntypedFormArray;

        if (!fromFa || !toFa) {
            return;
        }

        if (event.previousContainer === event.container) {
            const ctrl = toFa.at(event.previousIndex);
            toFa.removeAt(event.previousIndex);
            toFa.insert(event.currentIndex, ctrl);
        } else {
            const ctrl = fromFa.at(event.previousIndex);
            fromFa.removeAt(event.previousIndex);
            toFa.insert(event.currentIndex, ctrl);
        }

        fromFa.markAsDirty();
        toFa.markAsDirty();
        fromFa.updateValueAndValidity();
        toFa.updateValueAndValidity();
    }

    getCdcsFA(juryForm: UntypedFormGroup): UntypedFormArray {
        return juryForm.get('cdcs') as UntypedFormArray;
    }

    // ####################################
    // ############  Jures  ###############
    // ####################################

    addJure(indexJury: number) {
        const cdcsFA = this.formGroup.get(['jurys', indexJury, 'cdcs']) as UntypedFormArray;
        if (!cdcsFA || cdcsFA.length === 0) {
            throw new FunctionalError('COMMISSION_NO_CDC_SELECTED');
        }

        const modalRef = this.modalService.open(RechercheJureComponent, { size: 'lg', backdrop: 'static' });
        modalRef.componentInstance.idOrganisme = SessionContext.get('idOrganisme');
        modalRef.componentInstance.idsCdcs = cdcsFA.value.map((cdc: Cahier) => cdc.id);

        const juresFA = this.formGroup.get(['jurys', indexJury, 'jures']) as UntypedFormArray;
        modalRef.componentInstance.alreadySelectedJuresIds = (juresFA ? juresFA.value : []).map((jj: JureJury) => jj.jure.id);

        from(modalRef.result).subscribe((jures: any[]) => {
            if (!juresFA) {
                return;
            }

            jures.forEach((jure: any) => {
                const jureJury = Object.assign(new JureJury(), {
                    jure: jure,
                    college: jure.college,
                    statut: find(this.statutsJure, (st: any) => st.code === RefStatutJureCode.A_CONVOQUER)
                });

                juresFA.push(this.fb.control(jureJury));
            });

            juresFA.markAsDirty();
            juresFA.updateValueAndValidity();
        });
    }

    removeJure(indexJury: number, jure: JureJury) {
        const fa = this.formGroup.get(['jurys', indexJury, 'jures']) as UntypedFormArray;
        if (!fa) {
            return;
        }

        const values: JureJury[] = fa.value || [];
        const idx = values.indexOf(jure);

        if (idx >= 0) {
            fa.removeAt(idx);
            fa.markAsDirty();
            fa.updateValueAndValidity();
        }
    }

    canDropJure = (drag: CdkDrag<any>, drop: CdkDropList<any>) => {
        const dropFa = drop.data as UntypedFormArray;
        const draggedCtrl = drag.data; // AbstractControl
        const dragged = draggedCtrl ? draggedCtrl.value : null;

        const values = dropFa ? dropFa.value : [];
        if (!dragged || !dragged.jure || !dragged.college) {
            return true;
        }

        return (
            values.findIndex((item: any) => item && item.jure && item.college && item.jure.id === dragged.jure.id && item.college.id === dragged.college.id) ===
            -1
        );
    };

    moveJure(event: CdkDragDrop<UntypedFormArray>) {
        const fromFa = event.previousContainer.data as UntypedFormArray;
        const toFa = event.container.data as UntypedFormArray;

        if (!fromFa || !toFa) {
            return;
        }

        if (event.previousContainer === event.container) {
            const ctrl = toFa.at(event.previousIndex);
            toFa.removeAt(event.previousIndex);
            toFa.insert(event.currentIndex, ctrl);
        } else {
            const ctrl = fromFa.at(event.previousIndex);
            fromFa.removeAt(event.previousIndex);
            toFa.insert(event.currentIndex, ctrl);
        }

        fromFa.markAsDirty();
        toFa.markAsDirty();
        fromFa.updateValueAndValidity();
        toFa.updateValueAndValidity();
    }

    getJuresFA(juryForm: UntypedFormGroup): UntypedFormArray {
        return juryForm.get('jures') as UntypedFormArray;
    }

    // ####################################
    // ############  Echs   ###############
    // ####################################

    affecterEchantillons() {
        const modal = this.modalService.open(AffectationEchantillonsComponent, { windowClass: 'app-modal-1000px', backdrop: 'static' });
        const modalComponent = modal.componentInstance as AffectationEchantillonsComponent;
        modalComponent.echantillonsListe = this.jurys
            .map((fg, index) => {
                const echantillons = fg.get('echantillons').value as EchantillonJury[];
                echantillons.forEach(echantillon => (echantillon.numeroJury = index + 1));
                return echantillons;
            })
            .reduce((echantillons, part) => echantillons.concat(part), []);
        modalComponent.nbJurys = this.jurys.length;

        from(modal.result).subscribe(affectations => {
            this.jurys.forEach((fg, idx) => {
                let liste = fg.get('echantillons').value as EchantillonJury[];
                // liste = liste.filter(e => e.leurre);

                liste = affectations[idx + 1] || [];

                fg.get('echantillons').setValue(liste);
            });
        });
    }

    addEchantillons(indexJury: number) {
        const cdcsCtrl = this.formGroup.get(['jurys', indexJury, 'cdcs']) as UntypedFormArray;
        if (!cdcsCtrl || cdcsCtrl.length === 0) {
            throw new FunctionalError('COMMISSION_NO_CDC_SELECTED');
        }

        const modalRef = this.modalService.open(RechercheEchantillonsComponent, {
            windowClass: 'app-modal-1000px',
            backdrop: 'static'
        });

        modalRef.componentInstance.idOrganisme = SessionContext.get('idOrganisme');

        // cdcsCtrl.value = tableau de Cahier
        modalRef.componentInstance.idsCdcs = cdcsCtrl.value.map((cdc: Cahier) => cdc.id);

        // récupérer les ids déjà sélectionnés (tous jurys)
        const jurysFA = this.formGroup.get('jurys') as UntypedFormArray;
        const alreadySelectedEchantillonsIds = jurysFA.controls.reduce((ids: number[], juryCtrl: any) => {
            const juryForm = juryCtrl as UntypedFormArray;
            const echFA = juryForm.get('echantillons') as UntypedFormArray;
            const echs: EchantillonJury[] = echFA ? echFA.value : [];

            for (let i = 0; i < echs.length; i++) {
                const e = echs[i];
                if (e && e.echantillon && e.echantillon.id != null) {
                    ids.push(e.echantillon.id);
                }
            }
            return ids;
        }, []);

        modalRef.componentInstance.alreadySelectedEchantillonsIds = alreadySelectedEchantillonsIds;

        from(modalRef.result).subscribe((res: { echantillons?: Echantillon[]; leurre?: Leurre }) => {
            const echFA = this.formGroup.get(['jurys', indexJury, 'echantillons']) as UntypedFormArray;
            if (!echFA) {
                return;
            }

            if (res && res.echantillons && res.echantillons.length) {
                res.echantillons.forEach((echantillon: Echantillon) => {
                    const echJury = new EchantillonJury();
                    echJury.numeroAnonymat = (++this.indexLastEchantillon).toString();
                    echJury.echantillon = echantillon;

                    echFA.push(this.fb.control(echJury));
                });
            }

            if (res && res.leurre) {
                const echJury = new EchantillonJury();
                echJury.numeroAnonymat = (++this.indexLastEchantillon).toString();
                echJury.leurre = res.leurre;

                echFA.push(this.fb.control(echJury));
            }

            echFA.markAsDirty();
            echFA.updateValueAndValidity();
        });
    }

    moveEchantillon(event: CdkDragDrop<UntypedFormArray>) {
        const fromFa = event.previousContainer.data as UntypedFormArray;
        const toFa = event.container.data as UntypedFormArray;

        if (event.previousContainer === event.container) {
            const ctrl = toFa.at(event.previousIndex);
            toFa.removeAt(event.previousIndex);
            toFa.insert(event.currentIndex, ctrl);
        } else {
            const ctrl = fromFa.at(event.previousIndex);
            fromFa.removeAt(event.previousIndex);
            toFa.insert(event.currentIndex, ctrl);
        }

        fromFa.updateValueAndValidity();
        toFa.updateValueAndValidity();
    }

    getEchantillonsFA(juryForm: any): UntypedFormArray {
        if (!juryForm || typeof juryForm.get !== 'function') {
            return this.fb.array([]);
        }

        const c = juryForm.get('echantillons');
        if (c && c instanceof UntypedFormArray) {
            return c;
        }

        return this.fb.array([]);
    }

    removeEchantillon(indexJury: number, ctrl: AbstractControl) {
        const fa = this.formGroup.get(['jurys', indexJury, 'echantillons']) as UntypedFormArray;
        const i = fa.controls.indexOf(ctrl);

        if (i >= 0) {
            fa.removeAt(i);
            fa.markAsDirty();
            fa.updateValueAndValidity();
        }
    }

    // ####################################
    // ############   D&D   ###############
    // ####################################

    generateIdsList(prefix: string, nb: number) {
        return Array.from(Array(nb).keys()).map(i => prefix + '-' + i);
    }

    getCVI(echantillon: Echantillon): string {
        try {
            return echantillon.infosOperateur.find(infos => !!~infos.code.indexOf('CVI')).valeur;
        } catch (e) {
            return '';
        }
    }

    getRaisonSociale(echantillon: Echantillon): string {
        try {
            return echantillon.operateur.raisonSociale;
        } catch (e) {
            return '';
        }
    }

    public telechargerDocument() {
        const modalRef = this.modalService.open(TelechargerDocumentComponent, { size: 'lg', backdrop: 'static' });
        modalRef.componentInstance.commission = this.commission;
        modalRef.componentInstance.categorie = CategorieEnum.COMMISSION;
        modalRef.result.then(
            () => {},
            error => {
                throw error;
            }
        );
    }

    public envoyerDocumentsMail() {
        const modalRef = this.modalService.open(TelechargerDocumentComponent, { size: 'lg', backdrop: 'static' });
        modalRef.componentInstance.commission = this.commission;
        modalRef.componentInstance.categorie = CategorieEnum.COMMISSION;
        modalRef.componentInstance.commissionSendMail = true;
        modalRef.componentInstance.documentsIds = modalRef.result.then(
            () => {},
            error => {
                throw error;
            }
        );
    }

    public filterEchantillons(echantillons: EchantillonJury[]) {
        const modalRef = this.modalService.open(FiltreEchantillonsIgpComponent, { windowClass: 'app-modal-1000px', backdrop: 'static' });
        modalRef.componentInstance.echantillons = echantillons;
        // modalRef.result.then(
        //   () => { },
        //   error => { throw error; }
        // );
    }

    /**
     * Aller à l'export des échantillons
     */
    goToExporterEchantillons() {
        this.router.navigate(['main/export/generation-echantillon'], { state: { commission: this.commission.id } });
    }

    /**
     * Aller au résultat de la commission dans la page Suivi de contrôle
     */
    goToResultatCommission() {
        const idNatureControle = this.refNatures.find(nature => nature.code === TypeNature.ORGANOLEPTIQUE).id;
        this.router.navigate(['main/controles/suivi-controles'], { state: { controle: idNatureControle, commission: this.commission.id } });
    }

    normalizeJuryArrays(): void {
        const jurysFA = this.formGroup.get('jurys') as UntypedFormArray;

        jurysFA.controls.forEach((juryCtrl: any) => {
            const juryForm = juryCtrl as UntypedFormGroup;

            const echCtrl = juryForm.get('echantillons');
            if (echCtrl instanceof UntypedFormControl) {
                const arr = echCtrl.value || [];
                juryForm.setControl('echantillons', this.fb.array(arr.map((e: any) => this.fb.control(e))));
            }

            const cdcsCtrl = juryForm.get('cdcs');
            if (cdcsCtrl instanceof UntypedFormControl) {
                const arr = cdcsCtrl.value || [];
                juryForm.setControl('cdcs', this.fb.array(arr.map((c: any) => this.fb.control(c))));
            }

            const juresCtrl = juryForm.get('jures');
            if (juresCtrl instanceof UntypedFormControl) {
                const arr = juresCtrl.value || [];
                juryForm.setControl('jures', this.fb.array(arr.map((j: any) => this.fb.control(j))));
            }
        });
    }
}
